import { withRequestDeadline } from "../lib/requestDeadline";
import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { getAllValidConnectionsForUser } from "../lib/utils";
import { PlatformConnection } from "../models/Platform";

export function useWorkspaceConnections() {
  const { currentUser, activeWorkspaceId } = useAuth();
  const [connections, setConnections] = useState<PlatformConnection[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const attempt = useRef(0);
  const refresh = useCallback(async () => {
    const id = ++attempt.current;
    if (!currentUser || !activeWorkspaceId) {
      setConnections([]);
      setLoading(false);
      setError(null);
      return;
    }
    setLoading(true);
    try {
      const next = await withRequestDeadline(
        (signal) => getAllValidConnectionsForUser(activeWorkspaceId, signal),
        15000,
        "Connected accounts couldn’t be loaded. You can continue without connecting."
      );
      if (id !== attempt.current) return;
      setConnections(next);
      setError(null);
    } catch (refreshError) {
      if (id !== attempt.current) return;
      setConnections([]);
      setError(
        refreshError instanceof Error
          ? refreshError
          : new Error("Could not load connected accounts.")
      );
    } finally {
      if (id === attempt.current) setLoading(false);
    }
  }, [currentUser, activeWorkspaceId]);

  useEffect(() => {
    void refresh();
    return () => {
      attempt.current++;
    };
  }, [refresh]);

  return { connections, loading, error, refresh };
}
